import { useEffect, useRef } from 'react';
import { useAtom } from 'jotai';
import { useToast } from '@amzn/orion-components';
import { flashbarAtom, flashbarClearCounterAtom } from 'src/store/flashbarAtom';
import type { ToastVariant } from '@amzn/orion-components';
import type { FlashbarProps } from '@cloudscape-design/components';

/**
 * FlashbarToastBridge
 *
 * Watches `flashbarAtom` and forwards each new flash message to the Orion
 * toast system. Loading toasts (msg.loading === true) are persistent (no
 * duration) and are dismissed when EITHER:
 *   1. ANY non-loading flash message arrives (CHub's `addFlashbarMessage`
 *      flow uses different timestamp ids for the loading and the
 *      success/error message, so per-id dismissal alone wouldn't work — we
 *      always sweep all outstanding loading toasts when a non-loading
 *      message arrives)
 *   2. `clearFlashbarMessages()` is called (the page explicitly cleared the
 *      flashbar — bumps `flashbarClearCounterAtom`, and the bridge dismisses
 *      ALL outstanding loading toasts)
 *
 * IMPORTANT: this bridge is mounted at the app root (above the router) so
 * the same Orion ToastProvider context is available to every page.
 */
export function FlashbarToastBridge() {
  const [flashbarMessages, setFlashbarMessages] = useAtom(flashbarAtom);
  const [clearCounter] = useAtom(flashbarClearCounterAtom);
  const { addToast, removeToast } = useToast();

  // All outstanding loading toast ids. We sweep this set whenever a
  // non-loading message arrives or `clearFlashbarMessages()` is called.
  const loadingToastIds = useRef<Set<string>>(new Set());
  // Outstanding non-loading toast ids; a new non-loading message dismisses them
  // so only one shows at a time (no stacking).
  const activeToastIds = useRef<Set<string>>(new Set());
  // Track how many clears we've already processed so the effect only reacts
  // to NEW clear events (not the initial mount value of 0).
  const lastClearCounter = useRef<number>(clearCounter);

  // An open modal marks everything outside it inert, toasts included, so their close button stops
  // working. React Aria leaves elements flagged as its top layer alone.
  useEffect(() => {
    document
      .querySelector('[data-testid="toast-container"]')
      ?.setAttribute('data-react-aria-top-layer', 'true');
  }, []);

  // Dismiss all outstanding loading toasts when clearFlashbarMessages() fires
  useEffect(() => {
    if (clearCounter === lastClearCounter.current) return;
    lastClearCounter.current = clearCounter;

    loadingToastIds.current.forEach((toastId) => removeToast(toastId));
    loadingToastIds.current.clear();
    activeToastIds.current.forEach((toastId) => removeToast(toastId));
    activeToastIds.current.clear();
  }, [clearCounter, removeToast]);

  useEffect(() => {
    const messages = flashbarMessages as FlashbarProps.MessageDefinition[];
    if (messages.length === 0) return;

    for (const msg of messages) {
      const variant: ToastVariant =
        msg.type === 'success'
          ? 'success'
          : msg.type === 'error'
            ? 'error'
            : msg.type === 'warning'
              ? 'warning'
              : 'info';

      const isLoading = !!msg.loading;

      // Any non-loading toast dismisses ALL outstanding loading toasts. CHub's
      // data-loading flow emits a fresh timestamp id for the success message
      // (rather than reusing the loading id), so per-id dismissal would never
      // fire — sweeping all loading toasts on any non-loading event is the
      // intended behavior.
      if (!isLoading && loadingToastIds.current.size > 0) {
        loadingToastIds.current.forEach((tid) => removeToast(tid));
        loadingToastIds.current.clear();
      }

      // One non-loading toast at a time: dismiss the previous before adding.
      if (!isLoading && activeToastIds.current.size > 0) {
        activeToastIds.current.forEach((tid) => removeToast(tid));
        activeToastIds.current.clear();
      }

      const toastId = addToast({
        message: msg.content as string,
        variant,
        duration: isLoading ? 0 : 10000,
      });

      if (isLoading) {
        loadingToastIds.current.add(toastId);
      } else {
        activeToastIds.current.add(toastId);
      }
    }

    setFlashbarMessages([]);
  }, [flashbarMessages, addToast, removeToast, setFlashbarMessages]);

  return null;
}
